/**
 * The symbols the Currency and Accounting formats can wear.
 *
 * Excel's own list runs to several hundred entries because it is generated
 * per locale: "$ English (Australia)", "$ English (Belize)", "$ English
 * (Canada)" and so on, all of which put the same dollar sign in front of the
 * number. That length is an artefact of how it is built, not a feature — what
 * it buys is that any symbol anyone might want is in there somewhere.
 *
 * So this list is by currency rather than by locale: every circulating
 * currency, each once, with the symbol written the way that country writes
 * it. It is the same coverage without a hundred duplicate dollar signs, and
 * it is searchable by code, by symbol and by name, which Excel's is not.
 *
 * The first group is this office's own: the rupee, the dirham, the dollar and
 * the riyal are what its paperwork is denominated in, and putting them at the
 * top saves a scroll on every ledger.
 *
 * What is stored in a cell's style is the `symbol` string, which is what goes
 * in front of the figure. The code is kept for searching and for showing
 * beside it in the list.
 */
/**
 * Written in front of a figure, in the order the office uses them.
 *
 * "None" first, because Accounting with no symbol is a real and common
 * choice — it is what lines a column up without claiming a denomination.
 */
export const COMMON_CURRENCIES = [
    { code: '', symbol: '', name: 'None', minorUnits: 2 },
    { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee', minorUnits: 2 },
    { code: 'PKR', symbol: 'Rs', name: 'Pakistani Rupee (Rs)', minorUnits: 2 },
    { code: 'PKR', symbol: 'PKR', name: 'Pakistani Rupee (code)', minorUnits: 2 },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', minorUnits: 2 },
    { code: 'AED', symbol: 'AED', name: 'UAE Dirham (code)', minorUnits: 2 },
    { code: 'USD', symbol: '$', name: 'US Dollar', minorUnits: 2 },
    { code: 'USD', symbol: 'US$', name: 'US Dollar (US$)', minorUnits: 2 },
    { code: 'USD', symbol: 'USD', name: 'US Dollar (code)', minorUnits: 2 },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', minorUnits: 2 },
    { code: 'GBP', symbol: '£', name: 'Pound Sterling', minorUnits: 2 },
    { code: 'EUR', symbol: '€', name: 'Euro', minorUnits: 2 },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee', minorUnits: 2 },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', minorUnits: 2 },
];
/**
 * Everything else, alphabetically by name.
 *
 * Long on purpose. A trading house is sent an invoice in whatever the other
 * side banks in, and a list that stops at the obvious dozen is a list that
 * sends someone back to Excel.
 */
export const ALL_CURRENCIES = [
    { code: 'AFN', symbol: '؋', name: 'Afghan Afghani', minorUnits: 2 },
    { code: 'ALL', symbol: 'L', name: 'Albanian Lek', minorUnits: 2 },
    { code: 'AMD', symbol: '֏', name: 'Armenian Dram', minorUnits: 2 },
    { code: 'ANG', symbol: 'ƒ', name: 'Netherlands Antillean Guilder', minorUnits: 2 },
    { code: 'AOA', symbol: 'Kz', name: 'Angolan Kwanza', minorUnits: 2 },
    { code: 'ARS', symbol: '$', name: 'Argentine Peso', minorUnits: 2 },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', minorUnits: 2 },
    { code: 'AWG', symbol: 'ƒ', name: 'Aruban Florin', minorUnits: 2 },
    { code: 'AZN', symbol: '₼', name: 'Azerbaijani Manat', minorUnits: 2 },
    { code: 'BAM', symbol: 'KM', name: 'Bosnia-Herzegovina Convertible Mark', minorUnits: 2 },
    { code: 'BBD', symbol: 'Bds$', name: 'Barbadian Dollar', minorUnits: 2 },
    { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka', minorUnits: 2 },
    { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev', minorUnits: 2 },
    { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar', minorUnits: 3 },
    { code: 'BIF', symbol: 'FBu', name: 'Burundian Franc', minorUnits: 0 },
    { code: 'BMD', symbol: 'BD$', name: 'Bermudian Dollar', minorUnits: 2 },
    { code: 'BND', symbol: 'B$', name: 'Brunei Dollar', minorUnits: 2 },
    { code: 'BOB', symbol: 'Bs.', name: 'Bolivian Boliviano', minorUnits: 2 },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', minorUnits: 2 },
    { code: 'BSD', symbol: 'B$', name: 'Bahamian Dollar', minorUnits: 2 },
    { code: 'BTN', symbol: 'Nu.', name: 'Bhutanese Ngultrum', minorUnits: 2 },
    { code: 'BWP', symbol: 'P', name: 'Botswanan Pula', minorUnits: 2 },
    { code: 'BYN', symbol: 'Br', name: 'Belarusian Ruble', minorUnits: 2 },
    { code: 'BZD', symbol: 'BZ$', name: 'Belize Dollar', minorUnits: 2 },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', minorUnits: 2 },
    { code: 'CDF', symbol: 'FC', name: 'Congolese Franc', minorUnits: 2 },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', minorUnits: 2 },
    { code: 'CLP', symbol: '$', name: 'Chilean Peso', minorUnits: 0 },
    { code: 'COP', symbol: '$', name: 'Colombian Peso', minorUnits: 2 },
    { code: 'CRC', symbol: '₡', name: 'Costa Rican Colón', minorUnits: 2 },
    { code: 'CUP', symbol: '$', name: 'Cuban Peso', minorUnits: 2 },
    { code: 'CVE', symbol: '$', name: 'Cape Verdean Escudo', minorUnits: 2 },
    { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna', minorUnits: 2 },
    { code: 'DJF', symbol: 'Fdj', name: 'Djiboutian Franc', minorUnits: 0 },
    { code: 'DKK', symbol: 'kr', name: 'Danish Krone', minorUnits: 2 },
    { code: 'DOP', symbol: 'RD$', name: 'Dominican Peso', minorUnits: 2 },
    { code: 'DZD', symbol: 'د.ج', name: 'Algerian Dinar', minorUnits: 2 },
    { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound', minorUnits: 2 },
    { code: 'ERN', symbol: 'Nfk', name: 'Eritrean Nakfa', minorUnits: 2 },
    { code: 'ETB', symbol: 'Br', name: 'Ethiopian Birr', minorUnits: 2 },
    { code: 'FJD', symbol: 'FJ$', name: 'Fijian Dollar', minorUnits: 2 },
    { code: 'FKP', symbol: '£', name: 'Falkland Islands Pound', minorUnits: 2 },
    { code: 'GEL', symbol: '₾', name: 'Georgian Lari', minorUnits: 2 },
    { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi', minorUnits: 2 },
    { code: 'GIP', symbol: '£', name: 'Gibraltar Pound', minorUnits: 2 },
    { code: 'GMD', symbol: 'D', name: 'Gambian Dalasi', minorUnits: 2 },
    { code: 'GNF', symbol: 'FG', name: 'Guinean Franc', minorUnits: 0 },
    { code: 'GTQ', symbol: 'Q', name: 'Guatemalan Quetzal', minorUnits: 2 },
    { code: 'GYD', symbol: 'G$', name: 'Guyanaese Dollar', minorUnits: 2 },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', minorUnits: 2 },
    { code: 'HNL', symbol: 'L', name: 'Honduran Lempira', minorUnits: 2 },
    { code: 'HRK', symbol: 'kn', name: 'Croatian Kuna', minorUnits: 2 },
    { code: 'HTG', symbol: 'G', name: 'Haitian Gourde', minorUnits: 2 },
    { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint', minorUnits: 2 },
    { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', minorUnits: 2 },
    { code: 'ILS', symbol: '₪', name: 'Israeli New Shekel', minorUnits: 2 },
    { code: 'IQD', symbol: 'ع.د', name: 'Iraqi Dinar', minorUnits: 3 },
    { code: 'IRR', symbol: '﷼', name: 'Iranian Rial', minorUnits: 2 },
    { code: 'ISK', symbol: 'kr', name: 'Icelandic Króna', minorUnits: 0 },
    { code: 'JMD', symbol: 'J$', name: 'Jamaican Dollar', minorUnits: 2 },
    { code: 'JOD', symbol: 'د.ا', name: 'Jordanian Dinar', minorUnits: 3 },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen', minorUnits: 0 },
    { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', minorUnits: 2 },
    { code: 'KGS', symbol: 'с', name: 'Kyrgystani Som', minorUnits: 2 },
    { code: 'KHR', symbol: '៛', name: 'Cambodian Riel', minorUnits: 2 },
    { code: 'KMF', symbol: 'CF', name: 'Comorian Franc', minorUnits: 0 },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won', minorUnits: 0 },
    { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar', minorUnits: 3 },
    { code: 'KYD', symbol: 'CI$', name: 'Cayman Islands Dollar', minorUnits: 2 },
    { code: 'KZT', symbol: '₸', name: 'Kazakhstani Tenge', minorUnits: 2 },
    { code: 'LAK', symbol: '₭', name: 'Laotian Kip', minorUnits: 2 },
    { code: 'LBP', symbol: 'ل.ل', name: 'Lebanese Pound', minorUnits: 2 },
    { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee', minorUnits: 2 },
    { code: 'LRD', symbol: 'L$', name: 'Liberian Dollar', minorUnits: 2 },
    { code: 'LSL', symbol: 'L', name: 'Lesotho Loti', minorUnits: 2 },
    { code: 'LYD', symbol: 'ل.د', name: 'Libyan Dinar', minorUnits: 3 },
    { code: 'MAD', symbol: 'د.م.', name: 'Moroccan Dirham', minorUnits: 2 },
    { code: 'MDL', symbol: 'L', name: 'Moldovan Leu', minorUnits: 2 },
    { code: 'MGA', symbol: 'Ar', name: 'Malagasy Ariary', minorUnits: 2 },
    { code: 'MKD', symbol: 'ден', name: 'Macedonian Denar', minorUnits: 2 },
    { code: 'MMK', symbol: 'K', name: 'Myanmar Kyat', minorUnits: 2 },
    { code: 'MNT', symbol: '₮', name: 'Mongolian Tugrik', minorUnits: 2 },
    { code: 'MOP', symbol: 'MOP$', name: 'Macanese Pataca', minorUnits: 2 },
    { code: 'MRU', symbol: 'UM', name: 'Mauritanian Ouguiya', minorUnits: 2 },
    { code: 'MUR', symbol: '₨', name: 'Mauritian Rupee', minorUnits: 2 },
    { code: 'MVR', symbol: '.ރ', name: 'Maldivian Rufiyaa', minorUnits: 2 },
    { code: 'MWK', symbol: 'MK', name: 'Malawian Kwacha', minorUnits: 2 },
    { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso', minorUnits: 2 },
    { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', minorUnits: 2 },
    { code: 'MZN', symbol: 'MT', name: 'Mozambican Metical', minorUnits: 2 },
    { code: 'NAD', symbol: 'N$', name: 'Namibian Dollar', minorUnits: 2 },
    { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', minorUnits: 2 },
    { code: 'NIO', symbol: 'C$', name: 'Nicaraguan Córdoba', minorUnits: 2 },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', minorUnits: 2 },
    { code: 'NPR', symbol: '₨', name: 'Nepalese Rupee', minorUnits: 2 },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', minorUnits: 2 },
    { code: 'OMR', symbol: 'ر.ع.', name: 'Omani Rial', minorUnits: 3 },
    { code: 'PAB', symbol: 'B/.', name: 'Panamanian Balboa', minorUnits: 2 },
    { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol', minorUnits: 2 },
    { code: 'PGK', symbol: 'K', name: 'Papua New Guinean Kina', minorUnits: 2 },
    { code: 'PHP', symbol: '₱', name: 'Philippine Peso', minorUnits: 2 },
    { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', minorUnits: 2 },
    { code: 'PYG', symbol: '₲', name: 'Paraguayan Guarani', minorUnits: 0 },
    { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal', minorUnits: 2 },
    { code: 'RON', symbol: 'lei', name: 'Romanian Leu', minorUnits: 2 },
    { code: 'RSD', symbol: 'дин', name: 'Serbian Dinar', minorUnits: 2 },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble', minorUnits: 2 },
    { code: 'RWF', symbol: 'FRw', name: 'Rwandan Franc', minorUnits: 0 },
    { code: 'SBD', symbol: 'SI$', name: 'Solomon Islands Dollar', minorUnits: 2 },
    { code: 'SCR', symbol: '₨', name: 'Seychellois Rupee', minorUnits: 2 },
    { code: 'SDG', symbol: 'ج.س.', name: 'Sudanese Pound', minorUnits: 2 },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', minorUnits: 2 },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', minorUnits: 2 },
    { code: 'SHP', symbol: '£', name: 'Saint Helena Pound', minorUnits: 2 },
    { code: 'SLE', symbol: 'Le', name: 'Sierra Leonean Leone', minorUnits: 2 },
    { code: 'SOS', symbol: 'Sh', name: 'Somali Shilling', minorUnits: 2 },
    { code: 'SRD', symbol: '$', name: 'Surinamese Dollar', minorUnits: 2 },
    { code: 'SSP', symbol: '£', name: 'South Sudanese Pound', minorUnits: 2 },
    { code: 'STN', symbol: 'Db', name: 'São Tomé and Príncipe Dobra', minorUnits: 2 },
    { code: 'SYP', symbol: 'ل.س', name: 'Syrian Pound', minorUnits: 2 },
    { code: 'SZL', symbol: 'E', name: 'Swazi Lilangeni', minorUnits: 2 },
    { code: 'THB', symbol: '฿', name: 'Thai Baht', minorUnits: 2 },
    { code: 'TJS', symbol: 'SM', name: 'Tajikistani Somoni', minorUnits: 2 },
    { code: 'TMT', symbol: 'm', name: 'Turkmenistani Manat', minorUnits: 2 },
    { code: 'TND', symbol: 'د.ت', name: 'Tunisian Dinar', minorUnits: 3 },
    { code: 'TOP', symbol: 'T$', name: 'Tongan Paʻanga', minorUnits: 2 },
    { code: 'TRY', symbol: '₺', name: 'Turkish Lira', minorUnits: 2 },
    { code: 'TTD', symbol: 'TT$', name: 'Trinidad and Tobago Dollar', minorUnits: 2 },
    { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar', minorUnits: 2 },
    { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', minorUnits: 2 },
    { code: 'UAH', symbol: '₴', name: 'Ukrainian Hryvnia', minorUnits: 2 },
    { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', minorUnits: 0 },
    { code: 'UYU', symbol: '$U', name: 'Uruguayan Peso', minorUnits: 2 },
    { code: 'UZS', symbol: "so'm", name: 'Uzbekistani Som', minorUnits: 2 },
    { code: 'VES', symbol: 'Bs.', name: 'Venezuelan Bolívar', minorUnits: 2 },
    { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', minorUnits: 0 },
    { code: 'VUV', symbol: 'VT', name: 'Vanuatu Vatu', minorUnits: 0 },
    { code: 'WST', symbol: 'WS$', name: 'Samoan Tala', minorUnits: 2 },
    { code: 'XAF', symbol: 'FCFA', name: 'Central African CFA Franc', minorUnits: 0 },
    { code: 'XCD', symbol: 'EC$', name: 'East Caribbean Dollar', minorUnits: 2 },
    { code: 'XOF', symbol: 'CFA', name: 'West African CFA Franc', minorUnits: 0 },
    { code: 'XPF', symbol: '₣', name: 'CFP Franc', minorUnits: 0 },
    { code: 'YER', symbol: '﷼', name: 'Yemeni Rial', minorUnits: 2 },
    { code: 'ZAR', symbol: 'R', name: 'South African Rand', minorUnits: 2 },
    { code: 'ZMW', symbol: 'ZK', name: 'Zambian Kwacha', minorUnits: 2 },
    { code: 'ZWL', symbol: 'Z$', name: 'Zimbabwean Dollar', minorUnits: 2 },
];
/** The whole list as the dialog shows it: the office's own, then the rest. */
export const CURRENCY_OPTIONS = [...COMMON_CURRENCIES, ...ALL_CURRENCIES];
/**
 * The entries matching what someone has typed into the symbol box.
 *
 * Matches on code, symbol or name, so "rupee", "PKR" and "₨" all find the
 * same row, and an empty query gives the list back whole.
 */
export function searchCurrencies(query) {
    const needle = query.trim().toLowerCase();
    if (!needle)
        return CURRENCY_OPTIONS;
    return CURRENCY_OPTIONS.filter((entry) => entry.code.toLowerCase().includes(needle) ||
        entry.symbol.toLowerCase().includes(needle) ||
        entry.name.toLowerCase().includes(needle));
}
/**
 * One entry per ISO code, for everything that thinks in codes rather than
 * symbols: the money module, the importer's detection, the exporter's symbol
 * lookup.
 *
 * `CURRENCY_OPTIONS` above lists the same currency more than once on purpose —
 * the rupee is offered as ₨, as Rs and as PKR, because a picker should let
 * you choose how it is written. A code is a code, so the first spelling of
 * each wins here.
 */
export const ISO_CURRENCIES = (() => {
    const byCode = new Map();
    for (const entry of CURRENCY_OPTIONS) {
        if (!entry.code || byCode.has(entry.code))
            continue;
        byCode.set(entry.code, entry);
    }
    return [...byCode.values()].sort((a, b) => (a.code < b.code ? -1 : 1));
})();
const BY_CODE = new Map(ISO_CURRENCIES.map((entry) => [entry.code, entry]));
/** What a code is called and written as, or undefined if we do not know it. */
export function currencyByCode(code) {
    return BY_CODE.get(code.toUpperCase());
}
/** The symbol to print in front of a figure for a code, falling back to it. */
export function currencySymbol(code) {
    if (!code)
        return '';
    return BY_CODE.get(code.toUpperCase())?.symbol ?? code;
}
/**
 * The currency a piece of format text is denominated in.
 *
 * Reads an Excel number-format code — its literal text and the contents of
 * any `[$…]` section — and answers with an ISO code. Codes are matched before
 * symbols, and longer symbols before shorter ones, so "US$" is not read as a
 * bare dollar and "د.إ" is not read as an Arabic letter.
 *
 * Ambiguity is real and unavoidable: a lone "$" is used by a dozen countries
 * and "₨" by four. Where a symbol is shared, the office's own currency wins,
 * which is what `CURRENCY_OPTIONS` already puts first.
 */
/**
 * The Windows locale ids Excel writes into `[$symbol-nnn]`, for the symbols
 * that more than one country uses.
 *
 * The yen sign and the yuan sign are the same character, and so are four
 * different rupees and a dozen dollars. The symbol alone cannot settle it;
 * the locale beside it can, and Excel always writes one. Only the ambiguous
 * cases are listed — anything else is read from its symbol below.
 */
const LOCALE_CURRENCIES = {
    '411': 'JPY',
    '804': 'CNY',
    '404': 'TWD',
    '409': 'USD',
    '809': 'GBP',
    c09: 'AUD',
    '1009': 'CAD',
    '1409': 'NZD',
    '420': 'PKR',
    '439': 'INR',
    '4009': 'INR',
    '3801': 'AED',
    '401': 'SAR',
    '412': 'KRW',
    '41e': 'THB',
    '45a': 'SYP',
    '813': 'EUR',
    '407': 'EUR',
    '40c': 'EUR',
    '410': 'EUR',
};
export function detectCurrency(text) {
    // `[$¥-411]` is yen and `[$¥-804]` is yuan; the character is the same and
    // only the locale tells them apart.
    const tagged = /\[\$([^\]]*?)-([0-9a-fA-F]+)\]/.exec(text);
    // Only when the bracket actually carries a symbol. `[$-409]` is a bare
    // locale tag on a *date*, and reading a currency out of it is what once put
    // "SD" in front of one.
    const byLocale = tagged?.[1] ? LOCALE_CURRENCIES[(tagged[2] ?? '').toLowerCase()] : undefined;
    if (byLocale)
        return byLocale;
    return scanForCurrency(text);
}
function scanForCurrency(text) {
    /*
     * The `$` that opens a `[$…]` section is a marker, not a dollar sign:
     * `[$¥-411]` is yen and `[$-409]` is a bare locale tag that means nothing
     * about money at all. Dropping the marker before looking is what stops both
     * being read as dollars.
     */
    const scanned = text.replace(/\[\$/g, ' ');
    const haystack = scanned.toUpperCase();
    for (const entry of ISO_CURRENCIES) {
        if (new RegExp(`\\b${entry.code}\\b`).test(haystack))
            return entry.code;
    }
    const bySymbol = [...CURRENCY_OPTIONS]
        .filter((entry) => entry.code && entry.symbol)
        .sort((a, b) => b.symbol.length - a.symbol.length);
    for (const entry of bySymbol) {
        if (matchesSymbol(scanned, entry.symbol))
            return entry.code;
    }
    return undefined;
}
/**
 * Whether a piece of format text carries this symbol.
 *
 * A symbol written in letters — "Rs", "kr", "R", "m" — has to stand as its
 * own word, because a bare substring search finds the "m" of the Turkmen
 * manat inside "d-mmm-yy" and the "R" of the rand inside "MYR". A symbol made
 * of anything else is unambiguous wherever it appears: no date code contains
 * a ₨.
 */
function matchesSymbol(text, symbol) {
    const lettersOnly = /^\p{L}+$/u.test(symbol);
    if (!lettersOnly)
        return text.includes(symbol);
    const escaped = symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?<!\\p{L})${escaped}(?!\\p{L})`, 'u').test(text);
}
//# sourceMappingURL=currencies.js.map